import { z } from "zod"

export const AgencySchema = z.object({
  id: z.number(),
  name: z.string(),
  social_denomination: z.string().nullable(),
  district: z.number().nullable(),
  district_name: z.string(),
  RNAVT: z.string(),
  nif: z.string(),
  address: z.string(),
  postal_code: z.string(),
  locale: z.string(),
  phone_number: z.string().nullable(),
  email: z.string(),
  status: z.number(),
  is_master: z.boolean(),
  agency_id: z.number().nullable(),
  subdomain: z.string().nullable(),
  facebook_url: z.string().nullable(),
  instagram_url: z.string().nullable(),
  bd_egoi: z.string().nullable(),
  accession_date: z.string().nullable(),
  representative: z.string().nullable(),
  color: z.string().nullable(),
  logo_url: z.string(),
  logo_path: z.string().nullable(),
  facebook_qrcode_path: z.string().nullable(),
  subdomain_qrcode_path: z.string().nullable(),
  instagram_qrcode_path: z.string().nullable(),
})

export const AgencyDetailSchema = AgencySchema.extend({
  master_agency: z.object({ id: z.number(), name: z.string() }).nullable(),
  medias: z.array(z.object({
    id: z.number(),
    real_name: z.string(),
    mime_type: z.string(),
    url: z.string(),
  })),
})

export const AgencyFormSchema = z.object({
  name: z.string().min(1, "agencies.validation.nameRequired"),
  social_denomination: z.string().optional().default(""),
  district: z.number().nullable().optional(),
  RNAVT: z.string().min(1, "agencies.validation.RNAVTRequired"),
  nif: z.string().min(1, "agencies.validation.nifRequired"),
  address: z.string().min(1, "agencies.validation.addressRequired"),
  postal_code: z.string().min(1, "agencies.validation.postalCodeRequired"),
  locale: z.string().min(1, "agencies.validation.localeRequired"),
  phone_number: z.string().optional().default(""),
  email: z.string().email("agencies.validation.emailInvalid"),
  status: z.number().default(10),
  is_master: z.boolean().default(true),
  agency_id: z.number().nullable().optional().default(null),
  subdomain: z.string().optional().default(""),
  facebook_url: z.string().optional().default(""),
  instagram_url: z.string().optional().default(""),
  bd_egoi: z.string().optional().default(""),
  accession_date: z.string().optional().default(""),
  representative: z.string().optional().default(""),
  color: z.string().optional().default(""),
})

export const STATUS_ACTIVE = 10
export const STATUS_INACTIVE = 5

export const STATUS_MAP: Record<number, { label: string; color: string }> = {
  [STATUS_ACTIVE]: { label: "active", color: "green" },
  [STATUS_INACTIVE]: { label: "inactive", color: "red" },
}

export const DISTRICTS_PT: { value: number; label: string }[] = [
  { value: 0, label: "Açores" },
  { value: 1, label: "Aveiro" },
  { value: 2, label: "Beja" },
  { value: 3, label: "Braga" },
  { value: 4, label: "Bragança" },
  { value: 5, label: "Castelo Branco" },
  { value: 6, label: "Coimbra" },
  { value: 7, label: "Évora" },
  { value: 8, label: "Faro" },
  { value: 9, label: "Guarda" },
  { value: 10, label: "Leiria" },
  { value: 11, label: "Lisboa" },
  { value: 19, label: "Madeira" },
  { value: 12, label: "Portalegre" },
  { value: 13, label: "Porto" },
  { value: 14, label: "Santarém" },
  { value: 15, label: "Setúbal" },
  { value: 16, label: "Viana do Castelo" },
  { value: 17, label: "Vila Real" },
  { value: 18, label: "Viseu" },
]

export const DISTRICTS_BR: { value: number; label: string }[] = [
  { value: 12, label: "Acre" },
  { value: 27, label: "Alagoas" },
  { value: 16, label: "Amapá" },
  { value: 13, label: "Amazonas" },
  { value: 29, label: "Bahia" },
  { value: 23, label: "Ceará" },
  { value: 53, label: "Distrito Federal" },
  { value: 32, label: "Espírito Santo" },
  { value: 52, label: "Goiás" },
  { value: 21, label: "Maranhão" },
  { value: 51, label: "Mato Grosso" },
  { value: 50, label: "Mato Grosso do Sul" },
  { value: 31, label: "Minas Gerais" },
  { value: 15, label: "Pará" },
  { value: 25, label: "Paraíba" },
  { value: 41, label: "Paraná" },
  { value: 26, label: "Pernambuco" },
  { value: 22, label: "Piauí" },
  { value: 33, label: "Rio de Janeiro" },
  { value: 24, label: "Rio Grande do Norte" },
  { value: 43, label: "Rio Grande do Sul" },
  { value: 11, label: "Rondônia" },
  { value: 14, label: "Roraima" },
  { value: 42, label: "Santa Catarina" },
  { value: 35, label: "São Paulo" },
  { value: 28, label: "Sergipe" },
  { value: 17, label: "Tocantins" },
]

export type Agency = z.infer<typeof AgencySchema>
export type AgencyDetail = z.infer<typeof AgencyDetailSchema>
export type AgencyFormValues = z.infer<typeof AgencyFormSchema>
